"use client";

// La base de l'entreprise, lue comme un dossier : ce qu'on reprend en tête de chaque mémoire. Les rubriques se suivent
// par groupe ; chaque fiche montre ses données dans un cartouche, comme celui d'un plan, puis son texte, et s'édite
// seule, comme un chapitre du mémoire (son bloc, vérifié pendant la frappe). Les rubriques classées se regroupent par
// catégorie et se filtrent par mot-clé ; une rubrique vide invite à demander à l'agent. Ce que le dernier tour d'un
// agent a changé est marqué, avec son diff.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ErreurApi,
  type Champ,
  type Diagnostic,
  type Entreprise,
  type Fiche,
  type Rubrique,
} from "@/lib/api";
import { NOM_BASE } from "@/lib/textes";
import { DemanderAgent } from "./DemanderAgent";
import { Diff } from "./Diff";
import { EditeurKanon } from "./EditeurKanon";
import { Markdown } from "./Markdown";

type Erreur = { texte: string; diagnostics: Diagnostic[] };

function message(e: unknown): Erreur {
  if (e instanceof ErreurApi)
    return { texte: e.message, diagnostics: e.diagnostics };
  return { texte: "L'API ne répond pas.", diagnostics: [] };
}

// Les groupes de rubriques, dans l'ordre où l'API les donne.
export function groupes(entreprise: Entreprise): [string, Rubrique[]][] {
  const g = new Map<string, Rubrique[]>();
  for (const r of entreprise.rubriques)
    g.set(r.groupe, [...(g.get(r.groupe) ?? []), r]);
  return [...g];
}

// Les fiches qu'un tour d'agent a écrites ou changées : celles dont le bloc diffère de l'état d'avant le tour.
export function fichesChangees(
  entreprise: Entreprise,
  avant: Entreprise | null,
): Map<string, string> {
  const changees = new Map<string, string>(); // id → bloc d'avant ("" pour une fiche neuve)
  if (!avant) return changees;
  const blocs = new Map(
    avant.rubriques.flatMap((r) =>
      r.fiches.map((f) => [f.id, f.bloc] as const),
    ),
  );
  for (const r of entreprise.rubriques)
    for (const f of r.fiches) {
      const b = blocs.get(f.id);
      if (b !== f.bloc) changees.set(f.id, b ?? "");
    }
  return changees;
}

// Ce que l'agent peut faire d'une rubrique vide : le libellé du bouton et la demande qu'il pré-remplit.
const INVITATIONS: Record<string, { verbe: string; detail: string }> = {
  identite: {
    verbe: "compléter l'identité",
    detail:
      "raison sociale, forme juridique, SIRET, dirigeant, effectif, adresse, et une présentation de l'entreprise.",
  },
  certifications: {
    verbe: "recenser les certifications",
    detail:
      "les certifications et qualifications de l'entreprise, avec leur organisme, leur numéro et leur validité.",
  },
  "pieces-administratives": {
    verbe: "lister les pièces administratives",
    detail:
      "Kbis, attestations URSSAF et fiscale, assurances : avec l'organisme et la date de validité de chacune.",
  },
  references: {
    verbe: "ajouter des références",
    detail:
      "les chantiers passés à citer dans un mémoire, avec maître d'ouvrage, lieu, année, montant et lot.",
  },
  collaborateurs: {
    verbe: "présenter l'équipe",
    detail:
      "les collaborateurs clés, leur fonction, leurs qualifications et leur expérience.",
  },
  "co-traitants": {
    verbe: "recenser les co-traitants",
    detail:
      "les entreprises avec qui l'on répond en groupement, avec leur activité et un contact.",
  },
  "sous-traitants": {
    verbe: "recenser les sous-traitants",
    detail: "les sous-traitants habituels, avec leur activité et un contact.",
  },
  fournisseurs: {
    verbe: "recenser les fournisseurs",
    detail:
      "les fournisseurs et fabricants de matériel, avec ce qu'ils fournissent.",
  },
  materiel: {
    verbe: "lister le matériel",
    detail:
      "le matériel et les moyens de l'entreprise : véhicules, outillage, appareils de mesure.",
  },
  "fiches-techniques": {
    verbe: "rassembler les fiches techniques",
    detail:
      "les fiches techniques des produits que l'entreprise pose le plus souvent.",
  },
  "sections-types": {
    verbe: "écrire des sections types",
    detail:
      "des sections de mémoire réutilisables, tirées de ce qui revient d'un projet à l'autre.",
  },
  consignes: {
    verbe: "rédiger les consignes de rédaction",
    detail:
      "le ton, les mots à employer ou à éviter, la longueur, la mise en forme d'un mémoire.",
  },
};

function demandeRubrique(r: Rubrique): { libelle: string; texte: string } {
  const inv = INVITATIONS[r.id];
  const detail = inv?.detail ?? "ce qui manque.";
  return {
    libelle: `Demander à l'agent de ${inv?.verbe ?? "la remplir"}`,
    texte: `Complète la rubrique « ${r.titre} » du ${NOM_BASE} : ${detail} Appuie-toi sur les projets ; dis-moi ce que tu ne peux pas savoir.`,
  };
}

// Les champs déjà montrés autrement (la catégorie en intertitre, les mots-clés en pied de fiche).
const HORS_CARTOUCHE = new Set(["categorie", "tags"]);

// Le bloc d'une fiche neuve : le modèle de la rubrique, sinon un titre seul (l'id se tire du titre).
const modeleFiche = (r: Rubrique) => r.modele || "### Nouvelle fiche\n\n";

// Une rubrique sans modèle ne prend pas de fiche neuve : l'identité n'a qu'une fiche.
const ouverte = (r: Rubrique) => r.modele !== null;

const nomDe = (e: Entreprise) => e.nom ?? "l'entreprise";

type Props = {
  entreprise: Entreprise;
  avant: Entreprise | null; // la base au début du dernier tour d'un agent
  agentActif: boolean;
  onVue: (rubrique: string) => void; // la rubrique en haut de l'écran, pour le panneau
  onEnregistre: () => Promise<void>;
  onDemander: (texte: string) => void;
};

export function Socle({
  entreprise,
  avant,
  agentActif,
  onVue,
  onEnregistre,
  onDemander,
}: Props) {
  const zone = useRef<HTMLElement>(null);
  const changees = useMemo(
    () => fichesChangees(entreprise, avant),
    [entreprise, avant],
  );
  const total = entreprise.rubriques.reduce((n, r) => n + r.fiches.length, 0);
  const vides = entreprise.rubriques.filter(
    (r) => r.fiches.length === 0,
  ).length;

  // La rubrique lue : la première dont le titre a passé le haut de la zone.
  useEffect(() => {
    const racine = zone.current;
    if (!racine) return;
    const sections = [
      ...racine.querySelectorAll<HTMLElement>("[data-rubrique]"),
    ];
    const lire = () => {
      const haut = racine.getBoundingClientRect().top + 80;
      let courante = sections[0]?.dataset.rubrique;
      for (const s of sections)
        if (s.getBoundingClientRect().top <= haut)
          courante = s.dataset.rubrique;
      if (courante) onVue(courante);
    };
    lire();
    racine.addEventListener("scroll", lire, { passive: true });
    return () => racine.removeEventListener("scroll", lire);
  }, [entreprise, onVue]);

  return (
    <section className="vue socle" ref={zone}>
      <header className="socle-entete">
        <h2>
          {NOM_BASE} de {nomDe(entreprise)}
        </h2>
        <p className="discret">
          Ce que l&apos;entreprise dit d&apos;elle-même dans chaque mémoire. On
          l&apos;écrit avec les agents, fiche par fiche ; {total} fiche
          {total > 1 ? "s" : ""}
          {vides
            ? `, ${vides} rubrique${vides > 1 ? "s" : ""} encore vide${vides > 1 ? "s" : ""}`
            : ""}
          .
        </p>
        {changees.size > 0 && (
          <p className="socle-tour">
            Le dernier tour d&apos;un agent a écrit {changees.size} fiche
            {changees.size > 1 ? "s" : ""} : elles sont marquées d&apos;un
            trait.
          </p>
        )}
      </header>
      {groupes(entreprise).map(([groupe, rubriques]) => (
        <section key={groupe} className="socle-groupe" aria-label={groupe}>
          <h2 className="socle-groupe-titre">{groupe}</h2>
          {rubriques.map((r) => (
            <SectionRubrique
              key={r.id}
              r={r}
              changees={changees}
              agentActif={agentActif}
              onEnregistre={onEnregistre}
              onDemander={onDemander}
            />
          ))}
        </section>
      ))}
    </section>
  );
}

function SectionRubrique({
  r,
  changees,
  agentActif,
  onEnregistre,
  onDemander,
}: {
  r: Rubrique;
  changees: Map<string, string>;
  agentActif: boolean;
  onEnregistre: () => Promise<void>;
  onDemander: (texte: string) => void;
}) {
  const [tag, setTag] = useState<string | null>(null);
  const [ajout, setAjout] = useState(false);
  const tags = useMemo(() => {
    const n = new Map<string, number>();
    for (const f of r.fiches)
      for (const t of f.tags) n.set(t, (n.get(t) ?? 0) + 1);
    return [...n].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "fr"));
  }, [r.fiches]);
  const fiches = tag ? r.fiches.filter((f) => f.tags.includes(tag)) : r.fiches;
  const categories = useMemo(() => {
    const c = new Map<string, Fiche[]>();
    for (const f of fiches)
      c.set(f.categorie ?? "", [...(c.get(f.categorie ?? "") ?? []), f]);
    return [...c];
  }, [fiches]);
  const classees = categories.some(([c]) => c);
  const demande = demandeRubrique(r);
  const version = r.version;

  return (
    <section id={`rubrique-${r.id}`} data-rubrique={r.id} className="rubrique">
      <header className="rubrique-entete">
        <h3>{r.titre}</h3>
        {r.fiches.length > 0 && (
          <span className="rubrique-nombre">{r.fiches.length}</span>
        )}
        {r.fiches.length > 0 && !ajout && ouverte(r) && (
          <button
            className="lien rubrique-ajouter"
            disabled={agentActif}
            onClick={() => setAjout(true)}
          >
            Ajouter une fiche
          </button>
        )}
      </header>

      {tags.length > 1 && (
        <div
          className="filtres"
          role="group"
          aria-label={`Filtrer « ${r.titre} » par mot-clé`}
        >
          <button
            className={`filtre ${tag === null ? "actif" : ""}`}
            aria-pressed={tag === null}
            onClick={() => setTag(null)}
          >
            Toutes
          </button>
          {tags.map(([t, n]) => (
            <button
              key={t}
              className={`filtre ${tag === t ? "actif" : ""}`}
              aria-pressed={tag === t}
              onClick={() => setTag(tag === t ? null : t)}
            >
              {t} <span className="filtre-nombre">{n}</span>
            </button>
          ))}
        </div>
      )}

      {r.fiches.length === 0 && !ajout && (
        <div className="invitation">
          <p>Rien encore dans cette rubrique.</p>
          <div className="invitation-actions">
            <DemanderAgent onDemander={() => onDemander(demande.texte)}>
              {demande.libelle}
            </DemanderAgent>
            {ouverte(r) && (
              <button
                className="lien"
                disabled={agentActif}
                onClick={() => setAjout(true)}
              >
                L&apos;écrire soi-même
              </button>
            )}
          </div>
        </div>
      )}

      {categories.map(([categorie, liste]) => (
        <div key={categorie || "-"} className="categorie">
          {classees && (
            <h4 className="categorie-titre">{categorie || "Sans catégorie"}</h4>
          )}
          {liste.map((f) => (
            <VueFiche
              key={f.id}
              f={f}
              rubrique={r}
              avant={changees.get(f.id)}
              agentActif={agentActif}
              onEnregistre={onEnregistre}
              onDemander={onDemander}
              onTag={setTag}
            />
          ))}
        </div>
      ))}

      {ajout && (
        <NouvelleFiche
          r={r}
          version={version}
          agentActif={agentActif}
          onFin={async (cree) => {
            setAjout(false);
            if (cree) await onEnregistre();
          }}
        />
      )}
    </section>
  );
}

function VueFiche({
  f,
  rubrique,
  avant,
  agentActif,
  onEnregistre,
  onDemander,
  onTag,
}: {
  f: Fiche;
  rubrique: Rubrique;
  avant: string | undefined; // le bloc d'avant le dernier tour, s'il l'a changé
  agentActif: boolean;
  onEnregistre: () => Promise<void>;
  onDemander: (texte: string) => void;
  onTag: (tag: string) => void;
}) {
  const [edition, setEdition] = useState<string | null>(null);
  const [voirDiff, setVoirDiff] = useState(false);
  const [erreur, setErreur] = useState<Erreur | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const lint = useCallback(
    (bloc: string) => api.lintEntreprise({ fiche: f.id, bloc }),
    [f.id],
  );
  const champs = f.champs.filter(
    (c): c is Champ & { valeur: string } =>
      !HORS_CARTOUCHE.has(c.cle) && !!c.valeur?.trim(),
  );
  const manquants = f.champs.filter(
    (c) => !HORS_CARTOUCHE.has(c.cle) && !c.valeur?.trim(),
  ).length;
  const change = avant !== undefined;

  const editer = (texte: string | null) => {
    setErreur(null);
    setEdition(texte);
  };

  const enregistrer = async () => {
    if (edition === null) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await api.ecrireFiche(f.id, edition, f.version);
      setEdition(null);
      await onEnregistre();
    } catch (e) {
      setErreur(message(e));
    } finally {
      setEnvoi(false);
    }
  };

  const retirer = async () => {
    if (!window.confirm(`Retirer la fiche « ${f.titre} » du ${NOM_BASE} ?`))
      return;
    setErreur(null);
    try {
      await api.supprimerFiche(f.id, f.version);
      await onEnregistre();
    } catch (e) {
      setErreur(message(e));
    }
  };

  return (
    <article
      id={`fiche-${f.id}`}
      className={`fiche ${change ? "change" : ""} ${edition !== null ? "edition" : ""}`}
    >
      <header className="fiche-entete">
        <h4 className="fiche-titre">{f.titre}</h4>
        <div className="fiche-actions">
          {edition === null ? (
            <button
              className="lien"
              disabled={agentActif}
              onClick={() => editer(f.bloc)}
            >
              Modifier
            </button>
          ) : (
            <>
              <button disabled={envoi || agentActif} onClick={enregistrer}>
                {envoi ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button className="secondaire" onClick={() => editer(null)}>
                Annuler
              </button>
              <button
                className="lien danger"
                disabled={envoi || agentActif}
                onClick={retirer}
              >
                Retirer la fiche
              </button>
            </>
          )}
        </div>
      </header>

      {agentActif && edition !== null && (
        <p className="bandeau info">
          Un agent travaille : l&apos;enregistrement reprend à la fin de son
          tour.
        </p>
      )}
      {erreur && (
        <p className="bandeau danger">
          {erreur.texte}
          {erreur.diagnostics.length > 0 && edition === null && (
            <> — {erreur.diagnostics.map((d) => d.message).join(" ; ")}</>
          )}
        </p>
      )}

      {edition !== null ? (
        <EditeurKanon
          valeur={edition}
          onChange={setEdition}
          lint={lint}
          lignes={Math.min(24, Math.max(8, edition.split("\n").length + 2))}
          diagnosticsServeur={erreur?.diagnostics}
        />
      ) : (
        <>
          {change && (
            <div className="tour">
              <button
                className="lien"
                aria-expanded={voirDiff}
                onClick={() => setVoirDiff(!voirDiff)}
              >
                {voirDiff ? "▾" : "▸"} {avant ? "Modifiée" : "Écrite"} au
                dernier tour d&apos;un agent
              </button>
              {voirDiff && <Diff avant={avant ?? ""} apres={f.bloc} />}
            </div>
          )}
          {champs.length > 0 && (
            <dl className="cartouche">
              {champs.map((c) => (
                <div
                  key={c.cle}
                  className={c.valeur.length > 48 ? "large" : ""}
                >
                  <dt>{c.libelle}</dt>
                  <dd>{c.valeur}</dd>
                </div>
              ))}
            </dl>
          )}
          {manquants > 0 && champs.length > 0 && (
            <p className="fiche-manque">
              {manquants} donnée{manquants > 1 ? "s" : ""} à compléter
            </p>
          )}
          {f.texte.trim() && <Markdown texte={f.texte} />}
          {f.tags.length > 0 && (
            <ul className="fiche-tags" aria-label="Mots-clés">
              {f.tags.map((t) => (
                <li key={t}>
                  <button
                    className="etiquette"
                    title={`Ne montrer que « ${t} »`}
                    onClick={() => onTag(t)}
                  >
                    {t}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!champs.length && !f.texte.trim() && (
            <p className="discret">Fiche vide.</p>
          )}
          <DemanderAgent
            petit
            className="fiche-demander"
            onDemander={() =>
              onDemander(
                `Relis et complète la fiche « ${f.titre} » (rubrique « ${rubrique.titre} ») du ${NOM_BASE}. `,
              )
            }
          />
        </>
      )}
    </article>
  );
}

function NouvelleFiche({
  r,
  version,
  agentActif,
  onFin,
}: {
  r: Rubrique;
  version: string;
  agentActif: boolean;
  onFin: (cree: boolean) => Promise<void>;
}) {
  const [edition, setEdition] = useState(() => modeleFiche(r));
  const [erreur, setErreur] = useState<Erreur | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const lint = useCallback(
    (bloc: string) => api.lintEntreprise({ rubrique: r.id, bloc }),
    [r.id],
  );

  const creer = async () => {
    setEnvoi(true);
    setErreur(null);
    try {
      await api.creerFiche(r.id, edition, version);
      await onFin(true);
    } catch (e) {
      setErreur(message(e));
      setEnvoi(false);
    }
  };

  return (
    <article className="fiche edition nouvelle">
      <header className="fiche-entete">
        <h4 className="fiche-titre">Nouvelle fiche</h4>
        <div className="fiche-actions">
          <button disabled={envoi || agentActif} onClick={creer}>
            {envoi ? "Ajout…" : "Ajouter"}
          </button>
          <button className="secondaire" onClick={() => void onFin(false)}>
            Annuler
          </button>
        </div>
      </header>
      <p className="discret fiche-aide">
        Le titre après les ###, puis une donnée par ligne (« lieu: Versailles
        »), puis le texte.
      </p>
      {erreur && <p className="bandeau danger">{erreur.texte}</p>}
      <EditeurKanon
        valeur={edition}
        onChange={setEdition}
        lint={lint}
        lignes={10}
        diagnosticsServeur={erreur?.diagnostics}
      />
    </article>
  );
}

// Le panneau de droite de l'onglet Socle : les rubriques par groupe, avec leur nombre de fiches. Une rubrique vide a
// le cercle pointillé d'un chapitre « à écrire » ; celle qu'un agent vient de changer, la marque du tour.
export function ArbreSocle({
  entreprise,
  avant,
  rubrique,
}: {
  entreprise: Entreprise;
  avant: Entreprise | null;
  rubrique: string | null;
}) {
  const changees = fichesChangees(entreprise, avant);
  const aller = (id: string) => {
    const doux = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document
      .getElementById(`rubrique-${id}`)
      ?.scrollIntoView({ behavior: doux ? "smooth" : "auto", block: "start" });
  };
  return (
    <nav className="arbre" aria-label={`Rubriques du ${NOM_BASE}`}>
      <div className="arbre-entete">
        <strong>
          {NOM_BASE} de {nomDe(entreprise)}
        </strong>
        <span className="discret">
          {entreprise.rubriques.reduce((n, r) => n + r.fiches.length, 0)} fiches
        </span>
      </div>
      {groupes(entreprise).map(([groupe, rubriques]) => (
        <div key={groupe}>
          <div className="dossier">{groupe}</div>
          <ul>
            {rubriques.map((r) => (
              <li key={r.id}>
                <button
                  className={`arbre-ligne ${r.id === rubrique ? "actif" : ""}`}
                  aria-current={r.id === rubrique ? "location" : undefined}
                  onClick={() => aller(r.id)}
                >
                  <span
                    className={`pastille ${r.fiches.length ? "statut-valide" : "statut-a-ecrire"}`}
                    title={
                      r.fiches.length ? "Rubrique remplie" : "Rubrique vide"
                    }
                  />
                  <span className="arbre-titre">{r.titre}</span>
                  {r.fiches.some((f) => changees.has(f.id)) && (
                    <span
                      className="marque-tour"
                      title="Changée au dernier tour d'un agent"
                    >
                      ●
                    </span>
                  )}
                  <span className="points">{r.fiches.length || ""}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}
